import { cn } from "@/lib/utils";
import {
  Bot,
  Building2,
  FileText,
  FolderOpen,
  LineChart,
  Mail,
  StickyNote,
  User,
} from "lucide-react";

const tabs = [
  { id: "profile", label: "Profile", icon: User },
  // The fund's own analyses, presented as agents. The tab below it runs *our*
  // fixed AI reports, which is what it was called "Analysis" for — the id stays
  // `analysis` so existing ?tab= links and bookmarks keep working, and this
  // one stays `analysis-docs` for the same reason.
  { id: "analysis-docs", label: "Agents", icon: Bot },
  { id: "analysis", label: "AI Reports", icon: LineChart },
  { id: "reports", label: "Reports", icon: FileText },
  { id: "data-room", label: "Data Room", icon: FolderOpen },
  { id: "emails", label: "Emails", icon: Mail },
  { id: "notes", label: "Notes", icon: StickyNote },
  { id: "portco", label: "Portco", icon: Building2 },
] as const;

export type DealTab = (typeof tabs)[number]["id"];

interface DealTabBarProps {
  activeTab: DealTab;
  onTabChange: (tab: DealTab) => void;
  /**
   * Optional per-tab count badge (e.g. pending reviews on Profile).
   * Only shown when the count is > 0.
   */
  badges?: Partial<Record<DealTab, number>>;
  /**
   * Portco is portfolio-only — only rendered when the deal's status is
   * "Portfolio Companies".
   */
  showPortco?: boolean;
}

export function DealTabBar({
  activeTab,
  onTabChange,
  badges,
  showPortco,
}: DealTabBarProps) {
  const visibleTabs = tabs.filter(
    (tab) => tab.id !== "portco" || showPortco
  );
  return (
    // Tab bar sits on a `bg-card` strip against the warm page bg —
    // matches the dgpt-prototype's deal-detail tab layout. Active tab
    // is indicated by an absolute bottom bar (primary/ink color)
    // rather than a bordered-bottom toggle.
    <div className="flex items-center border-b border-border bg-card px-6">
      <div className="flex items-center gap-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const badgeCount = badges?.[tab.id] ?? 0;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
              {badgeCount > 0 && (
                <span
                  className="ml-1 font-mono tabular-nums text-[11px] leading-none text-amber-600"
                  title={`${badgeCount} pending`}
                  aria-label={`${badgeCount} pending`}
                >
                  {badgeCount}
                </span>
              )}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
